/**
 * Atalhos de teclado (I1) — a parte pura: quais teclas fazem o quê, e o leitor de sequência
 * (`g` seguido de `d`). Testável sem DOM; quem liga no `document` é `useGlobalShortcuts`.
 */

export type ShortcutAction = { kind: 'palette' } | { kind: 'help' } | { kind: 'go'; navId: string };

export interface ShortcutDef {
  keys: readonly string[];
  label: string;
  action: ShortcutAction;
}

/** Os destinos de `g _` apontam para ids de `navigation.ts` — o menu é a fonte única. */
export const GO_SHORTCUTS: readonly ShortcutDef[] = [
  { keys: ['g', 'd'], label: 'Ir para o Dashboard', action: { kind: 'go', navId: 'dashboard' } },
  { keys: ['g', 'c'], label: 'Ir para Campanhas', action: { kind: 'go', navId: 'campanhas' } },
  { keys: ['g', 'g'], label: 'Ir para Grupos', action: { kind: 'go', navId: 'grupos' } },
  { keys: ['g', 'i'], label: 'Ir para Incidentes', action: { kind: 'go', navId: 'incidentes' } },
  {
    keys: ['g', 'm'],
    label: 'Ir para Monitoradores',
    action: { kind: 'go', navId: 'monitoradores' },
  },
  {
    keys: ['g', 'r'],
    label: 'Ir para Redirecionadores',
    action: { kind: 'go', navId: 'redirecionadores' },
  },
];

export const ALL_SHORTCUTS: readonly ShortcutDef[] = [
  { keys: ['⌘', 'K'], label: 'Abrir a busca (Ctrl+K no Windows)', action: { kind: 'palette' } },
  { keys: ['/'], label: 'Abrir a busca', action: { kind: 'palette' } },
  ...GO_SHORTCUTS,
  { keys: ['?'], label: 'Ver os atalhos', action: { kind: 'help' } },
];

export interface KeyInput {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  /** O foco está num campo de texto (input, textarea, select, contenteditable). */
  inTextField: boolean;
  /** Milissegundos — injetado para o teste não depender do relógio. */
  at: number;
}

/** Janela entre o `g` e a segunda tecla. */
export const SEQUENCE_TIMEOUT_MS = 1200;

/**
 * Leitor de atalhos com estado (a primeira tecla da sequência). Devolve a ação a executar,
 * ou `null`. Dentro de campo de texto só ⌘K/Ctrl+K vale: `/` e `g` são letras de quem digita.
 */
export function createShortcutReader() {
  let pending: { key: string; at: number } | null = null;

  return function read(input: KeyInput): ShortcutAction | null {
    const key = input.key.length === 1 ? input.key.toLowerCase() : input.key;

    if ((input.metaKey || input.ctrlKey) && !input.altKey && key === 'k') {
      pending = null;
      return { kind: 'palette' };
    }
    if (input.inTextField || input.metaKey || input.ctrlKey || input.altKey) {
      pending = null;
      return null;
    }

    if (pending && input.at - pending.at <= SEQUENCE_TIMEOUT_MS) {
      const first = pending.key;
      pending = null;
      const match = GO_SHORTCUTS.find((s) => s.keys[0] === first && s.keys[1] === key);
      if (match) return match.action;
    }
    pending = null;

    if (input.key === '/') return { kind: 'palette' };
    if (input.key === '?') return { kind: 'help' };
    if (key === 'g') {
      pending = { key, at: input.at };
      return null;
    }
    return null;
  };
}

export function isTextField(target: EventTarget | null): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== 'string') return false;
  const el = target as HTMLElement;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag !== 'input') return false;
  const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
  return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'color', 'file'].includes(
    type,
  );
}
